import { Award, Plus } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { profileSectionEmptyCtaClass } from '@/lib/profile-section-styles'

export type JobOpeningSkillRead = {
  id: string
  skill_name: string
  requirement_type: string
}

export type JobOpeningSkillsReadSectionProps = {
  skills: JobOpeningSkillRead[]
  onEdit?: () => void
  editAriaLabel?: string
  editTitle?: string
  emptyMessage?: string
  /** `cta` — dashed Add control; `text` — muted sentence (default). */
  emptyVariant?: 'cta' | 'text'
  /**
   * `card` — internal EntityManageSection (icon, subtitle, coloured badges).
   * `plain` — public job page: heading only, no card, no icon, unfilled chips.
   */
  presentation?: 'card' | 'plain'
}

const PUBLIC_SKILL_CHIP_CLASS =
  'inline-flex max-w-full items-center rounded-full border border-foreground/30 bg-background px-3 py-1 text-sm font-medium'

function JobOpeningSkillLists({
  required,
  preferred,
  chip,
}: {
  required: JobOpeningSkillRead[]
  preferred: JobOpeningSkillRead[]
  chip: 'badge' | 'plain'
}) {
  return (
    <div className={chip === 'plain' ? 'space-y-4' : 'space-y-3'}>
      {required.length > 0 ? (
        <div className={chip === 'plain' ? '' : 'space-y-2'}>
          {chip === 'plain' ? (
            <h3 className="mb-2 text-sm font-medium leading-none">Required</h3>
          ) : (
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Required</p>
          )}
          <ul className="flex flex-wrap gap-2">
            {required.map((skill) => (
              <li key={skill.id}>
                {chip === 'plain' ? (
                  <span className={PUBLIC_SKILL_CHIP_CLASS}>{skill.skill_name}</span>
                ) : (
                  <Badge variant="defaultSoft">{skill.skill_name}</Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {preferred.length > 0 ? (
        <div className={chip === 'plain' ? '' : 'space-y-2'}>
          {chip === 'plain' ? (
            <h3 className="mb-2 text-sm font-medium leading-none">Preferred</h3>
          ) : (
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Preferred</p>
          )}
          <ul className="flex flex-wrap gap-2">
            {preferred.map((skill) => (
              <li key={skill.id}>
                {chip === 'plain' ? (
                  <span className={PUBLIC_SKILL_CHIP_CLASS}>{skill.skill_name}</span>
                ) : (
                  <Badge variant="outline">{skill.skill_name}</Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export function JobOpeningSkillsReadSection({
  skills,
  onEdit,
  editAriaLabel = 'Edit skills',
  editTitle = 'Edit skills',
  emptyMessage = 'No skills listed — edit to add.',
  emptyVariant = 'text',
  presentation = 'card',
}: JobOpeningSkillsReadSectionProps) {
  const required = skills.filter((s) => s.requirement_type === 'required')
  const preferred = skills.filter((s) => s.requirement_type !== 'required')
  const lists = (
    <JobOpeningSkillLists required={required} preferred={preferred} chip={presentation === 'plain' ? 'plain' : 'badge'} />
  )

  if (presentation === 'plain') {
    if (skills.length === 0) return null
    return (
      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Skills</h2>
        {lists}
      </section>
    )
  }
  if (skills.length === 0) {
    if (onEdit) {
      return (
        <EntityManageSection
          title="Skills"
          leadingIcon={Award}
          subtitle="What candidates should bring to the role"
          onEdit={onEdit}
          editAriaLabel={editAriaLabel}
          editTitle={editTitle}
        >
          {emptyVariant === 'cta' ? (
            <Button
              type="button"
              variant="outline"
              className={profileSectionEmptyCtaClass}
              onClick={onEdit}
            >
              <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
              {emptyMessage}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          )}
        </EntityManageSection>
      )
    }
    return null
  }

  return (
    <EntityManageSection
      title="Skills"
      leadingIcon={Award}
      subtitle="What candidates should bring to the role"
      onEdit={onEdit}
      editAriaLabel={editAriaLabel}
      editTitle={editTitle}
    >
      {lists}
    </EntityManageSection>
  )
}
